import { useRef, useState } from 'react'

interface OtpCodeInputProps {
  length?: number
  onChange: (code: string) => void
  disabled?: boolean
}

export const OtpCodeInput: React.FC<OtpCodeInputProps> = ({
  length = 6,
  onChange,
  disabled
}) => {
  const [digits, setDigits] = useState<string[]>(() => Array(length).fill(''))
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const emit = (nextDigits: string[]) => {
    setDigits(nextDigits)
    onChange(nextDigits.join(''))
  }

  const handleChange = (index: number, rawValue: string) => {
    const digit = rawValue.replace(/\D/g, '').slice(-1)
    const nextDigits = [...digits]
    nextDigits[index] = digit
    emit(nextDigits)

    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (!pasted) {
      return
    }
    event.preventDefault()

    const nextDigits = Array(length).fill('')
    for (let i = 0; i < pasted.length; i++) {
      nextDigits[i] = pasted[i]
    }
    emit(nextDigits)

    const nextFocusIndex = Math.min(pasted.length, length - 1)
    inputRefs.current[nextFocusIndex]?.focus()
  }

  return (
    <div className="grid grid-cols-6 gap-2">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el
          }}
          value={digit}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          disabled={disabled}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          className="h-[52px] text-center text-lg font-medium text-neutralBlack-900 bg-white border border-neutralBlack-200 rounded-xl outline-none focus:border-neutralBlack-900 disabled:opacity-50"
        />
      ))}
    </div>
  )
}
